import type { TenantTheme } from "@kovernow/types/tenant-web-config";
import { themeContrastFailures } from "./contrast.js";

/**
 * Theme loader (CAP-2): writes a tenant theme into the react-ui CSS
 * variables on `root` at start-up. Tokens the theme leaves out keep the
 * react-ui defaults (the CRM/checkout palette).
 */

export interface ApplyThemeOptions {
  /** Reject a theme that fails the contrast gate (development builds). */
  enforceContrast?: boolean;
  root?: HTMLElement;
  doc?: Document;
}

export class ThemeContrastError extends Error {
  constructor(public readonly failures: string[]) {
    super(`Theme fails WCAG AA contrast: ${failures.join("; ")}`);
    this.name = "ThemeContrastError";
  }
}

export function applyTheme(theme: TenantTheme, options: ApplyThemeOptions = {}): void {
  if (options.enforceContrast) {
    const failures = themeContrastFailures(theme);
    if (failures.length) {
      throw new ThemeContrastError(
        failures.map((f) => `${f.pair} ${f.ratio}:1 < ${f.required}:1`),
      );
    }
  }
  const doc = options.doc ?? document;
  const root = options.root ?? doc.documentElement;
  for (const [token, value] of Object.entries(theme.colors)) {
    if (value) root.style.setProperty(`--${token}`, value);
  }
  if (theme.rounded) {
    root.style.setProperty("--rounded-sm", theme.rounded.sm);
    root.style.setProperty("--rounded-md", theme.rounded.md);
    root.style.setProperty("--rounded-lg", theme.rounded.lg);
  }
  if (theme.ringOpacity !== undefined) {
    root.style.setProperty("--ring-opacity", String(theme.ringOpacity));
  }
  if (theme.fonts) {
    root.style.setProperty("--font-body", theme.fonts.body);
    root.style.setProperty("--font-heading", theme.fonts.heading);
    if (theme.fonts.googleFontsUrl && !doc.querySelector("link[data-tenant-fonts]")) {
      const link = doc.createElement("link");
      link.rel = "stylesheet";
      link.href = theme.fonts.googleFontsUrl;
      link.dataset.tenantFonts = "true";
      doc.head.appendChild(link);
    }
  }
}
